import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { staffMembers as defaultStaff } from '../data/staffData';
import { useSchoolInfo } from '../hooks/useSchoolInfo';

export default function StaffPage() {
  const schoolInfo = useSchoolInfo();
  const [staffList, setStaffList] = useState(defaultStaff);

  useEffect(() => {
    fetch('/_api/public/content/medarbejdere')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
          const mapped = sorted.map((item) => ({
            name: item.navn || item.name,
            role: item.stilling_tekst || item.terms?.["medarbejder-kategori"]?.[0] || item.role,
            image: item.billede?.[0] || item.image,
            subjects: item.fag_og_omraader || item.subjects,
            email: item.email,
            phone: item.telefon || item.phone
          }));
          setStaffList(mapped);
        }
      })
      .catch((err) => {
        console.warn('Kunne ikke hente medarbejdere fra CMS:', err);
      });
  }, []);

  return (
    <div className="bg-white pb-20">
      
      {/* Header Banner following container line */}
      <section className="bg-[#eef4e6] border-b border-[#d9e5d4] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
              Mød vores medarbejdere
            </h1>
            <p className="text-base sm:text-lg text-[#5A6961] leading-relaxed">
              Et dedikeret team af engagerede lærere, pædagoger og ledelse, der brænder for at skabe trygge og lærerige rammer for dit barn.
            </p>
          </div>
        </div>
      </section>

      {/* Staff Grid following container line */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {staffList.map((member) => (
            <div
              key={member.name}
              className="bg-white rounded-2xl border border-[#d9e5d4] overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div>
                <div className="aspect-[4/5] overflow-hidden bg-[#eef4e6]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-[center_top] transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h2 className="text-base font-bold text-[#1D2923] font-serif-heading">
                    {member.name}
                  </h2>
                  <div className="text-xs font-semibold text-black uppercase tracking-wide mt-0.5">
                    {member.role}
                  </div>
                  {member.subjects && (
                    <p className="text-xs text-[#5A6961] mt-2 leading-relaxed">
                      {member.subjects}
                    </p>
                  )}
                </div>
              </div>

              {(member.email || member.phone) && (
                <div className="px-5 pb-5 pt-2 border-t border-[#d9e5d4] text-xs text-[#5A6961] space-y-1">
                  {member.email && (
                    <div className="truncate">
                      <a href={`mailto:${member.email}`} className="hover:text-black font-semibold whitespace-nowrap">
                        {member.email}
                      </a>
                    </div>
                  )}
                  {member.phone && (
                    <div>
                      <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="hover:text-black font-semibold whitespace-nowrap">
                        {member.phone}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact info box */}
        <div className="mt-16 bg-[#eef4e6] rounded-3xl p-8 sm:p-10 border border-[#d9e5d4] text-center max-w-2xl mx-auto space-y-4">
          <h3 className="text-xl sm:text-2xl font-bold text-[#1D2923] font-serif-heading">
            Har du spørgsmål til en medarbejder eller ledelsen?
          </h3>
          <p className="text-sm text-[#5A6961] leading-relaxed">
            Du er altid velkommen til at kontakte skolens administration eller ledelse direkte.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <a
              href={`tel:${schoolInfo.phoneRaw}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold text-white bg-[#264A35] hover:bg-[#1A3626] whitespace-nowrap shadow-xs transition-all"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring {schoolInfo.phone}</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] transition-all"
            >
              <span>Gå til kontakt</span>
            </Link>
          </div>
        </div>

      </section>

    </div>
  );
}
